import { groupRepo } from "@repositories";
import { CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState, type FC } from "react";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { useAddGroupToOpportunity } from "../../queries/events.query";
import { CcButton, CcEmpty } from "./CallCenterUi";
import { CC, ccFieldSx } from "./call-center-ui.theme";

export const CallCenterGroupAssign: FC<{
  opportunityId: string;
  matchWeight: number;
}> = ({ opportunityId, matchWeight }) => {
  const { currOrganization } = useOrganization();
  const { mutateAsync: addGroup, isPending } = useAddGroupToOpportunity();
  const [query, setQuery] = useState("");

  const groups = useQuery({
    queryKey: ["call-center-groups", currOrganization?.id, query],
    enabled: !!currOrganization?.id,
    queryFn: () =>
      groupRepo.find({
        where: {
          organizationId: currOrganization!.id,
          isDeleted: false,
          volunteerOrganizationId: null,
          ...(query.trim()
            ? { name: { $contains: query.trim() } }
            : {}),
        },
        orderBy: { name: "asc" },
        limit: 12,
        include: { admin: true },
      }),
  });

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }} data-tour="cc-group-assign">
      <Stack gap={0.5} sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Typography sx={{ fontFamily: CC.font, fontWeight: 700, color: CC.ink }}>
          איזו קבוצה לשבץ
        </Typography>
        <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted, fontSize: 13.5 }}>
          מחפשים לפי שם. אחרי שהקבוצה הסכימה, שבצו אותה למשימה.
        </Typography>
        <TextField
          label="שם קבוצה"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ ...ccFieldSx, mt: 0.5 }}
        />
      </Stack>
      <Stack sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {groups.isLoading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : null}
        {!groups.isLoading && (groups.data?.length ?? 0) === 0 ? (
          <CcEmpty
            title="אין קבוצה מתאימה"
            body="אפשר לעבור לחיפוש ולפתוח את טבלת המשימות."
          />
        ) : null}
        {(groups.data ?? []).map((group) => (
          <Stack
            key={group.id}
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{ px: 2, py: 1.25, borderTop: `1px solid ${CC.line}` }}
          >
            <Stack sx={{ flex: 1, minWidth: 0 }} gap={0.25}>
              <Typography sx={{ fontFamily: CC.font, fontWeight: 700, color: CC.ink }}>
                {group.name}
              </Typography>
              <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted, fontSize: 13 }}>
                {[
                  group.size ? `${group.size} משתתפים` : null,
                  group.groupCoordinator || group.admin?.name || null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "בלי מתאם"}
              </Typography>
            </Stack>
            <CcButton
              disabled={isPending}
              onClick={() =>
                void addGroup({
                  opportunityId,
                  groupIds: [group.id],
                  matchWeight,
                })
              }
            >
              שבץ קבוצה
            </CcButton>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
